// StoryMore.jsx
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { color } from '../style/variables.js';

import FeedComponent from '../container/FeedComponent.js';
import MessageCard from '../container/MessageCard.js';
import WithIntersectionObserver from '../component/WithIntersectionObserver.jsx';
import HydrationBoundary from '../component/HydrationBoundary.jsx';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';
import { CategoryLayout } from '../resource/feedConstants.js';

import Spinning from '../style/Spinning.js';
import { headerHeight } from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';

import BackIcon from '../../img/ic_topbar_back.svg';
import LoadingImage from '../../img/loading.png';

const isClient = getIsInBrowserMainThread();

class StoryMore extends Component {
  nextTick = null;
  componentWillUnmount() {
    clearTimeout(this.nextTick);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'StoryMore';
    }
  }
  renderLoader() {
    const LoadingImageSrc = LoadingImage.src || LoadingImage;

    return (
      <MessageCardContainer>
        <MessageCardWrapper>
          <Loader>
            <Loading src={LoadingImageSrc} title="loading" alt="loading" />
          </Loader>
        </MessageCardWrapper>
      </MessageCardContainer>
    );
  }
  renderMessages() {
    const { listPath, category, isShorts, messageIds } = this.props;
    const shouldHydrate = !isClient || !window.__IS_HYDRATED__;
    return messageIds.map((messageId, index) => {
      return (
        <MessageCardContainer key={messageId}>
          <WithIntersectionObserver threshold={0.25}>
            {({ isIntersecting }) => {
              if (shouldHydrate) {
                return (
                  <HydrationBoundary
                    shouldHydrate={isIntersecting}
                    wrapper={<MessageCardWrapper />}
                  >
                    <MessageCard
                      id={messageId}
                      listPath={listPath}
                      categoryIndex={index}
                      categoryId={category}
                      itemIndexInCategory={index}
                      isShorts={isShorts}
                      shouldWrapLink
                      shouldShowUser
                      shouldTrackEvent
                      shouldUseLazyThumbnail={false}
                    />
                  </HydrationBoundary>
                );
              }
              return (
                <MessageCardWrapper>
                  {isIntersecting ? (
                    <MessageCard
                      id={messageId}
                      listPath={listPath}
                      categoryIndex={index}
                      categoryId={category}
                      itemIndexInCategory={index}
                      isShorts={isShorts}
                      shouldWrapLink
                      shouldShowUser
                      shouldTrackEvent
                      shouldUseLazyThumbnail={false}
                    />
                  ) : (
                    <MessageCard id={null} isShorts={isShorts} shouldShowUser />
                  )}
                </MessageCardWrapper>
              );
            }}
          </WithIntersectionObserver>
        </MessageCardContainer>
      );
    });
  }
  render() {
    const { category, titleI18nId, goBack, t } = this.props;
    const isFollowingStoryMore = 'pseudo_following_stories' === category;

    return (
      <StyledStoryMore>
        <Header>
          <HeaderLeft>
            <GoBack onClick={goBack}>
              <img src={BackIcon} title="back" />
            </GoBack>
          </HeaderLeft>
          <HeaderTitle>
            {t(titleI18nId, { ns: TranslationNamespace.FEED })}
          </HeaderTitle>
        </Header>
        <Main>
          <InfiniteScrollerContainer>
            <FeedComponent
              category={category}
              layout={CategoryLayout.GRID}
              shouldShowTitle={false}
              shouldShowMoreLink={false}
              isInPaginationMode={!isFollowingStoryMore}
            />
          </InfiniteScrollerContainer>
        </Main>
      </StyledStoryMore>
    );
  }
}

StoryMore.propTypes = {
  category: PropTypes.string,
  titleI18nId: PropTypes.string.isRequired,
  listPath: PropTypes.array,
  messageIds: PropTypes.arrayOf(PropTypes.string.isRequired),
  isShorts: PropTypes.bool,
  goBack: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

StoryMore.defaultProps = {
  category: '',
  descriptionI18nId: '',
  listPath: null,
  messageIds: [],
  isShorts: false,
};

const StyledStoryMore = styled.div`
  position: relative;
  padding-top: var(--space-to-pinned-notification);
`;
const Header = styled.header`
  position: fixed;
  z-index: 2;
  border-bottom: 1px solid rgba(100, 100, 100, 0.5);
  display: flex;
  align-items: center;
  padding-left: 60px;
  width: 100%;
  height: ${headerHeight}px;
  top: var(--space-to-pinned-notification);
  background-color: ${color.grey['900']};
  font-size: 16px;
  font-weight: 600;
`;

const HeaderTitle = styled.h1`
  margin: 0px;
  margin-left: 4px;
  font-size: 18px;
  font-weight: 600;
`;

const HeaderLeft = styled.div`
  position: absolute;
  top: 50%;
  left: 16px;
  display: flex;
  align-items: center;
  transform: translateY(-50%);
`;

const GoBack = styled.button`
  ${ResetButtonStyle};
  flex: none;
  height: 32px;
  :hover {
    opacity: 0.7;
  }
  & > img {
    height: 100%;
    vertical-align: top;
  }
`;

const InfiniteScrollerContainer = styled.div`
  padding-top: ${headerHeight + 1}px;
`;

const MessageCardContainer = styled.div`
  position: relative;
  padding-top: 142%;
`;

const MessageCardWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
`;

const Loader = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #646464;
  background-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3),
    rgba(0, 0, 0, 0.05) 17%,
    rgba(0, 0, 0, 0.3) 53%,
    rgba(0, 0, 0, 0.5)
  );
  border-radius: 4px;
`;

const Loading = styled.img`
  height: auto;
  width: 50%;
  animation: 1.4s infinite ${Spinning};
`;

const Main = styled.main`
  max-width: 960px;
  margin: 0 auto;
`;

export default withTranslation()(StoryMore);
